import { Fragment, useMemo } from 'react';
import { observer } from 'mobx-react';

import { URL_PRODUCT_PERMISSIONS_DOC } from '@/catalog/constants/urls';
import BaseGlobalAccessModal from '@/security/components/BaseGlobalAccessModal/BaseGlobalAccessModal';
import GlobalAccessDataAccessSelect from '@/security/components/BaseGlobalAccessModal/GlobalAccessDataAccessSelect/GlobalAccessDataAccessSelect';
import GlobalAccessLevelRadio from '@/security/components/BaseGlobalAccessModal/GlobalAccessLevelRadio/GlobalAccessLevelRadio';
import GlobalAccessModalFooter from '@/security/components/BaseGlobalAccessModal/GlobalAccessModalFooter/GlobalAccessModalFooter';
import RecursiveUpdateSelect from '@/security/components/BaseGlobalAccessModal/RecursiveUpdateSelect/RecursiveUpdateSelect';

import { getAccessByType } from '../../../constants/access';
import { useGlobalAccessModalStore } from '../GlobalAccessModalStore';

interface AccessModalContentProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function AccessModalContent(props: AccessModalContentProps) {
  const { isOpen, onClose, onSuccess } = props;

  const {
    isUpdating,
    shouldShowDataAccess,
    type,
    entitiesCount,
    accessValues,
    recursive,
    accessDiff,
    dataAccessDiff,
    setAccess,
    setRecursive,
    updateAccessList,
    reset,
  } = useGlobalAccessModalStore();

  function onChangeAccess(nextLevel: number) {
    setAccess(nextLevel);

    setRecursive(true);
  }

  function onUpdateAccess() {
    updateAccessList().then(() => {
      onCloseHandler();
      onSuccess?.();
    });
  }

  function onCloseHandler() {
    reset();
    onClose();
  }

  const accessValuesSet = useMemo(() => new Set(accessValues), [accessValues]);

  return (
    <BaseGlobalAccessModal
      disableClose={isUpdating}
      onClose={onCloseHandler}
      isOpen={isOpen}
      title={`Share Global Access${entitiesCount > 1 ? ` (${entitiesCount} ${type}s will be affected)` : ''}`}
      leftColumnTitle="General"
      leftColumn={getAccessByType(type).map((level) => {
        const isChecked = accessValues.includes(level.id);

        return (
          <GlobalAccessLevelRadio
            aria-label={level.title}
            access={level.id}
            key={level.id}
            checked={isChecked}
            description={level.description}
            disabled={isUpdating}
            isAmbient={accessValuesSet.size > 1 && isChecked}
            onChange={onChangeAccess}
            title={level.title}
            type={type}
          />
        );
      })}
      rightColumnTitle={type === 'product' ? 'Cut Data' : 'Project Data'}
      rightColumn={shouldShowDataAccess && <GlobalAccessDataAccessSelect />}
      bottom={(
        <Fragment>
          {type === 'project' && <RecursiveUpdateSelect />}

          <GlobalAccessModalFooter
            docHref={URL_PRODUCT_PERMISSIONS_DOC}
            isDisabled={isUpdating}
            isLoading={isUpdating}
            isSubmitDisabled={!(recursive || accessDiff || dataAccessDiff)}
            onClose={onCloseHandler}
            onSubmit={onUpdateAccess}
          />
        </Fragment>
      )}
    />
  );
}

export default observer(AccessModalContent);
